import type { Card } from '../../engine/card.js';
import type { GameState, Pile } from '../../engine/game-state.js';
import type { Move } from '../../engine/moves.js';
import type { SolitaireRules } from '../../engine/rules.js';

// Aces Up (a.k.a. Aces High / Idiot's Delight). Four tableau piles, a stock,
// and a single discard pile (modelled as a foundation). Aces are HIGH.
export const TABLEAU_IDS = ['t1', 't2', 't3', 't4'] as const;
export const DISCARD_ID = 'foundation';

// Ace ranks as 14 (highest); everything else keeps its face rank.
function value(card: Card): number {
  return card.rank === 1 ? 14 : card.rank;
}

function emptyPile(id: string, type: Pile['type']): Pile {
  return { id, type, cards: [] };
}

function setupAcesUp(deck: Card[]): GameState {
  const piles: Record<string, Pile> = {};
  for (const id of TABLEAU_IDS) piles[id] = emptyPile(id, 'tableau');
  piles.stock = emptyPile('stock', 'stock');
  piles[DISCARD_ID] = emptyPile(DISCARD_ID, 'foundation');

  const d = deck.slice();
  // One face-up card to each pile, the rest face-down in the stock.
  for (const id of TABLEAU_IDS) {
    const c = d.shift()!;
    c.faceUp = true;
    piles[id]!.cards.push(c);
  }
  for (const c of d) {
    c.faceUp = false;
    piles.stock!.cards.push(c);
  }
  return { piles, movesCount: 0 };
}

// A top card can be discarded when another pile's top card is the SAME suit and
// a HIGHER value (aces high). The lower card is the one removed.
function canDiscard(state: GameState, fromPile: Pile, card: Card): boolean {
  for (const id of TABLEAU_IDS) {
    if (id === fromPile.id) continue;
    const other = state.piles[id]!;
    const otop = other.cards[other.cards.length - 1];
    if (!otop) continue;
    if (otop.suit === card.suit && value(otop) > value(card)) return true;
  }
  return false;
}

export const acesupRules: SolitaireRules = {
  id: 'acesup',

  setup(deck) {
    return setupAcesUp(deck);
  },

  canMove(state, move: Move) {
    const from = state.piles[move.from];
    const to = state.piles[move.to];
    if (!from || !to) return false;
    if (from === to) return false;

    // Only the top card of a tableau pile can move.
    if (from.type !== 'tableau') return false;
    const top = from.cards[from.cards.length - 1];
    if (!top || top.id !== move.cardId) return false;

    // Relocate a top card onto an empty tableau pile.
    if (to.type === 'tableau') {
      return to.cards.length === 0;
    }
    // Discard to the pile when a same-suit higher card is exposed elsewhere.
    if (to.id === DISCARD_ID) {
      return canDiscard(state, from, top);
    }
    return false;
  },

  isWon(state) {
    if (state.piles.stock!.cards.length > 0) return false;
    // Exactly the four aces remain, one per pile.
    for (const id of TABLEAU_IDS) {
      const pile = state.piles[id]!;
      if (pile.cards.length !== 1) return false;
      if (pile.cards[0]!.rank !== 1) return false;
    }
    return true;
  },

  isLost(state) {
    if (state.piles.stock!.cards.length > 0) return false; // a deal is always available
    if (acesupRules.isWon(state)) return false;
    return !hasAnyMove(state);
  },
};

// Deal one card face-up to each of the four piles. Returns false on empty stock.
export function dealRow(state: GameState): boolean {
  const stock = state.piles.stock!;
  if (stock.cards.length === 0) return false;
  for (const id of TABLEAU_IDS) {
    if (stock.cards.length === 0) break;
    const c = stock.cards.pop()!;
    c.faceUp = true;
    state.piles[id]!.cards.push(c);
  }
  return true;
}

// Whether a *useful* move exists: any discard, or a relocation that exposes a
// new card (an empty pile to receive from a pile of depth ≥ 2). Shuffling a
// lone card between empties changes nothing, so it doesn't count.
export function hasAnyMove(state: GameState): boolean {
  for (const id of TABLEAU_IDS) {
    const pile = state.piles[id]!;
    const top = pile.cards[pile.cards.length - 1];
    if (top && canDiscard(state, pile, top)) return true;
  }
  const hasEmpty = TABLEAU_IDS.some((id) => state.piles[id]!.cards.length === 0);
  const hasDeep = TABLEAU_IDS.some((id) => state.piles[id]!.cards.length >= 2);
  return hasEmpty && hasDeep;
}
